import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import VideoPlayer from '../pages/VideoPlayer';
import useProgress from '../ProgressUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle, faHeart, faLink, faChevronLeft, faChevronRight, faHome } from '@fortawesome/free-solid-svg-icons';
import './JourneyModal.scss';
import {
  moduleLessonsByModuleID,
  getUserProgress,
  getLessonProgress,
  getModuleProgress,
  listLessonProgresses,
  lessonProgressesByModuleProgressID,
  getFAQItem,
  listLessonFAQItems,
  listVideoURLs,
  getLesson,
} from '../graphql/queries';
import {
  updateLessonProgress,
  createModuleProgress,
  createLessonProgress,
  updateModuleProgress,
  createUserProgress,
} from '../graphql/mutations';
import { Auth } from 'aws-amplify';
import Tools from './Tools';
import Thumbnail from "./Thumbnail";

const JourneyModal = ({
  isOpen,
  onRequestClose,
  courseData,
  courseConfig,
  favorites,
  onFavoriteToggle,
  instructors,
  videoURLsData: propVideoURLsData,
}) => {
  const {
    lessonProgress,
    moduleProgress,
    courseProgress,
    updateLessonProgressStatus,
    isLoading: isLoadingProgress,
    fetchProgress
  } = useProgress(courseData?.id, courseData);

  const [currentLessonIndex, setCurrentLessonIndex] = useState(null);
  const [flattenedLessons, setFlattenedLessons] = useState([]);
  const [isSliderVisible, setIsSliderVisible] = useState(true);
  const [posterUrls, setPosterUrls] = useState({});
  const [isFAQVisible, setIsFAQVisible] = useState(false);
  const [isToolsVisible, setIsToolsVisible] = useState(false);
  const [firstIncompleteLessonIndex, setFirstIncompleteLessonIndex] = useState(null);
  const [instructorImages, setInstructorImages] = useState({});
  const [videoURLsData, setVideoURLsData] = useState(propVideoURLsData || null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const [selectedCaptions, setSelectedCaptions] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [isLoadingVideoURLs, setIsLoadingVideoURLs] = useState(false);
  const [currentInstructor, setCurrentInstructor] = useState(null);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isLocalLoading, setIsLocalLoading] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const sliderRef = useRef(null);

  const currentLesson = flattenedLessons[currentLessonIndex];
  const progress = currentLesson ? lessonProgress[currentLesson.id] : null;

  // A day can hold more than one lesson, so day numbers come from lesson order
  const getDayNumber = (lesson, index) => lesson?.lessonOrder ?? index + 1;
  const totalDays = new Set(flattenedLessons.map(getDayNumber)).size;
  const currentDay = getDayNumber(currentLesson, currentLessonIndex);

  const isLoading = isLoadingProgress || isLocalLoading;

  // Add these state variables and refs near the top of the component
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollIntervalRef = useRef(null);
  const scrollSpeedRef = useRef(300); // ms between slides

  // Update videoURLsData when prop changes
  useEffect(() => {
    if (propVideoURLsData) {
      setVideoURLsData(propVideoURLsData);
    }
  }, [propVideoURLsData]);

  // Consolidate data fetching into a single effect
  useEffect(() => {
    const fetchAllVideoURLs = async () => {
        if (!isOpen) return;
        
        // If videoURLsData is provided as prop, don't fetch
        if (propVideoURLsData) {
            setVideoURLsData(propVideoURLsData);
            return;
        }
        
        setIsLoadingVideoURLs(true);
        let allItems = [];
        let nextToken = null;
        
        try {
            do {
                const videoUrlResponse = await API.graphql(
                    graphqlOperation(listVideoURLs, { 
                        limit: 10000,
                        nextToken: nextToken
                    })
                );
                
                const items = videoUrlResponse.data.listVideoURLs.items;
                nextToken = videoUrlResponse.data.listVideoURLs.nextToken;
                allItems = [...allItems, ...items];

            } while (nextToken);

            // Cache the video URLs data
            setVideoURLsData(allItems);
        } catch (error) {
            console.error("JourneyModal - Error fetching video URLs:", error);
        } finally {
            setIsLoadingVideoURLs(false);
        }
    };

    // Only fetch if we don't already have the data and no prop is provided
    if (isOpen && (!videoURLsData || videoURLsData.length === 0) && !propVideoURLsData) {
        fetchAllVideoURLs();
    }
}, [isOpen, propVideoURLsData]); // Depend on propVideoURLsData

  // Flatten the course structure into a sequential journey
  useEffect(() => {
    if (courseData?.modules && videoURLsData) {
      const fetchLessonsForModules = async () => {
        try {
          const sortedModules = [...courseData.modules].sort((a, b) => a.order - b.order);
          let allLessons = [];
          let lessonIndex = 0;

          for (const courseModule of sortedModules) {
            const moduleLessonsResponse = await API.graphql(
              graphqlOperation(moduleLessonsByModuleID, {
                moduleID: courseModule.id,
                filter: { _deleted: { ne: true } },
                limit: 1000,
              })
            );

            const moduleLessonItems = moduleLessonsResponse.data.moduleLessonsByModuleID.items
              .filter(item => !item._deleted)
              .sort((a, b) => a.order - b.order);

            const moduleLessons = await Promise.all(
              moduleLessonItems.map(async (moduleLesson) => {
                const lessonResponse = await API.graphql(
                  graphqlOperation(getLesson, { id: moduleLesson.lessonID })
                );
                const lesson = lessonResponse.data.getLesson;

                if (!lesson || lesson._deleted) return null;

                // Add additional tracking information
                return {
                  ...lesson,
                  moduleID: courseModule.id,
                  moduleOrder: courseModule.order,
                  lessonOrder: moduleLesson.order,
                  overallIndex: lessonIndex++, // Add overall position in journey
                  moduleTitle: courseModule.title, // Add module title for reference
                  faqItems: await fetchFAQItems(moduleLesson.lessonID),
                };
              })
            );

            allLessons = [...allLessons, ...moduleLessons.filter(lesson => lesson !== null)];
          }

          // Sort by module order first, then lesson order
          const sortedLessons = allLessons.sort((a, b) => {
            if (a.moduleOrder !== b.moduleOrder) {
              return a.moduleOrder - b.moduleOrder;
            }
            return a.lessonOrder - b.lessonOrder;
          });

          setFlattenedLessons(sortedLessons);
        } catch (error) {
          console.error('Error fetching lessons:', error);
        }
      };

      fetchLessonsForModules();
    }
  }, [courseData, videoURLsData]);

  // Fetch poster URLs
  useEffect(() => {
    const fetchPosterUrls = async () => {
      const urls = {};
      for (const lesson of flattenedLessons) {
        if (lesson.video?.poster) {
          try {
            const url = await Storage.get(lesson.video.poster, { level: "public" });
            urls[lesson.video.id] = url;
          } catch (error) {
            console.error("Error fetching poster URL:", error);
          }
        }
      }
      setPosterUrls(urls);
    };

    if (flattenedLessons.length > 0) {
      fetchPosterUrls();
    }
  }, [flattenedLessons]);

  // Fetch instructor images
  useEffect(() => {
    const fetchInstructorImages = async () => {
      const urls = {};
      for (const instructor of instructors) {
        if (instructor.image) {
          try {
            const url = await Storage.get(instructor.image, { level: "public" });
            urls[instructor.id] = url;
          } catch (error) {
            console.error("Error fetching instructor image URL:", error);
          }
        }
      }
      setInstructorImages(urls);
    };

    if (instructors.length > 0) {
      fetchInstructorImages();
    }
  }, [instructors]);

  // Initialize progress for modules
  useEffect(() => {
    const initializeProgress = async () => {
      if (!courseData?.modules) return;

      try {
        const user = await Auth.currentAuthenticatedUser();
        const userSub = user.attributes.sub;

        await Promise.all(courseData.modules.map(async (module) => {
          const moduleProgressId = `${userSub}-${module.id}`;

          try {
            const moduleProgressResponse = await API.graphql(
              graphqlOperation(getModuleProgress, { id: moduleProgressId })
            );

            if (!moduleProgressResponse.data.getModuleProgress) {
              await API.graphql(
                graphqlOperation(createModuleProgress, {
                  input: {
                    id: moduleProgressId,
                    userID: userSub,
                    moduleID: module.id,
                    progressPercentage: 0,
                    completed: false,
                  },
                })
              );
            }
          } catch (error) {
            console.error(`Error initializing progress for module ${module.id}:`, error);
          }
        }));
      } catch (error) {
        console.error('Error initializing module progress:', error);
      }
    };

    initializeProgress();
  }, [courseData]);

  // Find the first incomplete lesson index
  useEffect(() => {
    const findFirstIncomplete = () => {
      const index = flattenedLessons.findIndex(
        lesson => !lessonProgress[lesson.id]?.completed
      );
      return index === -1 ? flattenedLessons.length - 1 : index;
    };

    setFirstIncompleteLessonIndex(findFirstIncomplete());
  }, [flattenedLessons, lessonProgress]);

  // Consolidate lesson selection and video loading
  useEffect(() => {
    if (!isOpen || !flattenedLessons.length || !videoURLsData) {
        return;
    }

    const initializeLesson = async () => {
        setIsLocalLoading(true);
        setIsDataReady(false);

        try {
            const firstIncompleteIndex = flattenedLessons.findIndex(
                lesson => !lessonProgress[lesson.id]?.completed
            );
            const targetIndex = firstIncompleteIndex >= 0 ? firstIncompleteIndex : 0;
            
            setCurrentLessonIndex(targetIndex);
            const lesson = flattenedLessons[targetIndex];
            
            if (lesson) {
                await handleVideoSelect(lesson);
                setIsDataReady(true);
            }
        } catch (error) {
            console.error('Error initializing lesson:', error);
        } finally {
            setIsLocalLoading(false);
        }
    };

    initializeLesson();
}, [isOpen, flattenedLessons, videoURLsData, lessonProgress]);

  // Handle video selection
  const handleVideoSelect = async (lesson) => {
    if (!videoURLsData) {
        return;
    }
    
    if (!lesson.video?.url) {
        return;
    }

    try {
        const urlParts = lesson.video.url.split('/');
        const filenamePart = urlParts[urlParts.length - 1];
        
        // Remove 'Videos/' prefix if present
        const cleanVideoPath = lesson.video.url.replace(/^Videos\//, '');
        
        const matchedVideo = videoURLsData.find((item) => {
            // Try matching MP4 field first
            if (item.MP4) {
                const itemUrlParts = item.MP4.split("/");
                const itemFilenamePart = itemUrlParts[itemUrlParts.length - 1];
                if (itemFilenamePart === filenamePart) return true;
            }
            
            // Try matching srcvideo field if no MP4 match
            if (item.srcvideo) {
                // Remove 'Videos/' prefix if present in srcvideo
                const cleanSrcVideo = item.srcvideo.replace(/^Videos\//, '');
                if (cleanSrcVideo === cleanVideoPath) return true;
            }
            
            return false;
        });

        const instructor = instructors.find(
            (instructor) => instructor.id === lesson.video?.teacherVideosId
        );

        if (matchedVideo) {
            setSelectedCaptions(matchedVideo.MP4 ? `${matchedVideo.MP4}.vtt` : null);
            setSelectedVideoUrl(matchedVideo.HLS || matchedVideo.MP4);
            setCurrentInstructor(instructor || null);
        } else {
            setSelectedCaptions(null);
            setSelectedVideoUrl(null);
            setCurrentInstructor(null);
        }
    } catch (error) {
        setSelectedCaptions(null);
        setSelectedVideoUrl(null);
        setCurrentInstructor(null);
    }
  };

  // Handle progress update
  const handleProgressUpdate = async (lessonId, completed) => {
    try {
      setIsLoadingVideo(true);
      const currentLesson = flattenedLessons[currentLessonIndex];
      
      //console.log('Updating progress for lesson:', lessonId, 'completed:', completed);
      
      // Update progress and wait for confirmation
      const updateSuccess = await updateLessonProgressStatus(
        lessonId,
        currentLesson.moduleID,
        completed,
        completed ? 100 : 0
      );

      if (updateSuccess && completed) {
        // Only advance if update was successful and lesson was marked complete
        const nextIndex = currentLessonIndex + 1;
        if (nextIndex < flattenedLessons.length) {
          setCurrentLessonIndex(nextIndex);
          await handleVideoSelect(flattenedLessons[nextIndex]);
          if (sliderRef.current) {
            sliderRef.current.slickGoTo(nextIndex);
          }
        }
      }

    } catch (error) {
      console.error('Error in handleProgressUpdate:', error);
    } finally {
      setIsLoadingVideo(false);
    }
  };

  // Handle lesson change
  const handleLessonChange = (newIndex) => {
    const currentLesson = flattenedLessons[currentLessonIndex];
    const targetLesson = flattenedLessons[newIndex];
    
    // Check both lesson and module progress
    const currentModuleProgress = moduleProgress[currentLesson.moduleID];
    const targetModuleProgress = moduleProgress[targetLesson.moduleID];
    const currentLessonCompleted = lessonProgress[currentLesson.id]?.completed;
    const targetLessonCompleted = lessonProgress[targetLesson.id]?.completed;

    // Allow navigation if:
    // 1. Moving backwards
    // 2. Target lesson is completed
    // 3. Current lesson is completed
    // 4. Target module's previous modules are completed
    if (
      newIndex < currentLessonIndex || 
      getDayNumber(targetLesson, newIndex) <= currentDay ||
      targetLessonCompleted || 
      currentLessonCompleted ||
      (targetModuleProgress && targetLesson.moduleOrder > 1 && 
       moduleProgress[flattenedLessons[newIndex - 1]?.moduleID]?.completed)
    ) {
      setIsLoadingVideo(true);
      setCurrentLessonIndex(newIndex);
      handleVideoSelect(targetLesson).then(() => {
        setIsLoadingVideo(false);
        if (sliderRef.current) {
          sliderRef.current.slickGoTo(newIndex);
        }
      });
    } else {
      alert("Please complete the current lesson before moving forward.");
      // Don't try to reset the slider position - let it stay where it is
      // The slider will naturally stay on the current slide
    }
  };

  // Handle current lesson click
  const handleCurrentLessonClick = async () => {
    if (
        firstIncompleteLessonIndex === flattenedLessons.length - 1 &&
        lessonProgress[flattenedLessons[firstIncompleteLessonIndex].id]?.completed
    ) {
        alert("Congratulations! You've completed all lessons in this journey!");
        return;
    }

    const targetLesson = flattenedLessons[firstIncompleteLessonIndex];
    
    setIsLoadingVideo(true);
    setCurrentLessonIndex(firstIncompleteLessonIndex);
    
    try {
        await handleVideoSelect(targetLesson);
        if (sliderRef.current) {
            sliderRef.current.slickGoTo(firstIncompleteLessonIndex);
            // Force a reflow to ensure proper positioning
            setTimeout(() => {
                if (sliderRef.current) {
                    sliderRef.current.slickGoTo(firstIncompleteLessonIndex);
                }
            }, 100);
        }
    } catch (error) {
        console.error("Error switching to current lesson:", error);
    } finally {
        setIsLoadingVideo(false);
    }
};

  // Handle body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Add these functions before the return statement
  const startScrolling = (direction) => {
    if (scrollIntervalRef.current) return;
    
    const scroll = () => {
        if (direction === 'next') {
            sliderRef.current?.slickNext();
        } else {
            sliderRef.current?.slickPrev();
        }
    };

    // Initial scroll
    scroll();
    
    // Continue scrolling while button is held
    scrollIntervalRef.current = setInterval(() => {
        scroll();
        // Gradually increase scroll speed
        scrollSpeedRef.current = Math.max(100, scrollSpeedRef.current - 50);
    }, scrollSpeedRef.current);
    
    setIsScrolling(true);
};

const stopScrolling = () => {
    if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
        scrollSpeedRef.current = 100; // Reset speed
        setIsScrolling(false);
    }
};

  // Update the slider settings
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 150,
    slidesToShow: 3,
    slidesToScroll: 1,
    swipe: true,
    swipeToSlide: true,
    touchThreshold: 10,
    centerMode: true,
    centerPadding: '0px',
    arrows: false,
    initialSlide: currentLessonIndex,

    beforeChange: (oldIndex, newIndex) => {
      if (sliderRef.current && sliderRef.current.innerSlider && 
          sliderRef.current.innerSlider.list && 
          sliderRef.current.innerSlider.list.querySelector('.slick-track')) {
        const slides = sliderRef.current.innerSlider.list.querySelector('.slick-track').children;
        if (slides && slides[newIndex]) {
          slides[newIndex].style.pointerEvents = 'none';
          setTimeout(() => {
            if (slides[newIndex]) {
              slides[newIndex].style.pointerEvents = 'auto';
            }
          }, 100);
        }
      }
      

    },
    afterChange: (currentIndex) => {
      // Check if we're at the end of the course
      const atEnd = currentIndex >= flattenedLessons.length - 2;
      setIsAtEnd(atEnd);
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          centerMode: true,
          swipe: true,
          touchThreshold: 10,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          swipe: true,
          touchThreshold: 10,
          arrows: false,
        },
      },
    ],
  };

  // Update the navigation buttons
  <div className="journey-navigation">
    <button 
        className="journey-nav-button"
        onMouseDown={() => startScrolling('prev')}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
        onTouchStart={() => startScrolling('prev')}
        onTouchEnd={stopScrolling}
    >
        <FontAwesomeIcon icon={faChevronLeft} /> Previous
    </button>

    <button 
        className="journey-nav-button current" 
        onClick={handleCurrentLessonClick}
    >
        <FontAwesomeIcon icon={faHome} /> Current Lesson
    </button>

    <button 
        className="journey-nav-button"
        onMouseDown={() => startScrolling('next')}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
        onTouchStart={() => startScrolling('next')}
        onTouchEnd={stopScrolling}
    >
        Next <FontAwesomeIcon icon={faChevronRight} />
    </button>
  </div>

  // Add cleanup in useEffect
  useEffect(() => {
    return () => {
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
        }
    };
}, []);

  // Handle share click
  const handleShareClick = () => {
    const shareUrl = `${window.location.origin}/play/${currentLesson?.video?.id}`;

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          alert("Share link copied to clipboard!");
        })
        .catch((err) => {
          fallbackCopyTextToClipboard(shareUrl);
        });
    } else {
      fallbackCopyTextToClipboard(shareUrl);
    }
  };

  // Fallback function for older browsers
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed"; // Prevent scrolling to bottom
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      const msg = successful
        ? "Share link copied to clipboard!"
        : `Share link: ${text}`;
      alert(msg);
    } catch (err) {
      alert(`Share link: ${text}`);
    }

    document.body.removeChild(textArea);
  };

  // Add this helper function near the top of the component
  const isJourneyComplete = () => {
    return flattenedLessons.every(lesson => lessonProgress[lesson.id]?.completed);
  };

  const fetchFAQItems = async (lessonId) => {
    try {
      const faqItemsResponse = await API.graphql(
        graphqlOperation(listLessonFAQItems, {
          filter: {
            lessonID: { eq: lessonId },
            _deleted: { ne: true },
          },
        })
      );

      const faqItems = await Promise.all(
        faqItemsResponse.data.listLessonFAQItems.items
          .filter(item => !item._deleted)
          .map(async (item) => {
            const faqItemResponse = await API.graphql(
              graphqlOperation(getFAQItem, { id: item.faqItemID })
            );
            return faqItemResponse.data.getFAQItem;
          })
      );

      return faqItems.filter(item => item && !item._deleted);
    } catch (error) {
      console.error('Error fetching FAQ items:', error);
      return [];
    }
  };

  // Update the initialization effect
  useEffect(() => {
    if (!isOpen || !courseData?.modules) return;

    const initializeJourney = async () => {
      setIsLocalLoading(true);
      try {
        const user = await Auth.currentAuthenticatedUser();
        const userSub = user.attributes.sub;
        const userProgressId = `${userSub}-${courseData.id}`;

        // First ensure UserProgress exists
        const userProgressResponse = await API.graphql(
          graphqlOperation(getUserProgress, { id: userProgressId })
        );

        if (!userProgressResponse.data.getUserProgress) {
          await API.graphql(
            graphqlOperation(createUserProgress, {
              input: {
                id: userProgressId,
                userID: userSub,
                courseID: courseData.id,
              },
            })
          );
        }

        // Initialize all module progress records first
        await Promise.all(courseData.modules.map(async (module) => {
          const moduleProgressId = `${userSub}-${module.id}`;
          try {
            const moduleProgressResponse = await API.graphql(
              graphqlOperation(getModuleProgress, { id: moduleProgressId })
            );

            if (!moduleProgressResponse.data.getModuleProgress) {
              await API.graphql(
                graphqlOperation(createModuleProgress, {
                  input: {
                    id: moduleProgressId,
                    userID: userSub,
                    moduleID: module.id,
                    userProgressID: userProgressId,
                    progressPercentage: 0,
                    completed: false,
                  },
                })
              );
            }
          } catch (error) {
            console.error(`Error initializing progress for module ${module.id}:`, error);
          }
        }));

        // Now fetch lessons and progress data
        if (courseData?.modules && videoURLsData) {
          const sortedModules = [...courseData.modules].sort((a, b) => a.order - b.order);
          let allLessons = [];
          let lessonIndex = 0;

          for (const courseModule of sortedModules) {
            const moduleLessonsResponse = await API.graphql(
              graphqlOperation(moduleLessonsByModuleID, {
                moduleID: courseModule.id,
                filter: { _deleted: { ne: true } },
                limit: 1000,
              })
            );

            const moduleLessonItems = moduleLessonsResponse.data.moduleLessonsByModuleID.items
              .filter(item => !item._deleted)
              .sort((a, b) => a.order - b.order);

            const moduleLessons = await Promise.all(
              moduleLessonItems.map(async (moduleLesson) => {
                const lessonResponse = await API.graphql(
                  graphqlOperation(getLesson, { id: moduleLesson.lessonID })
                );
                const lesson = lessonResponse.data.getLesson;

                if (!lesson || lesson._deleted) return null;

                // Add additional tracking information
                return {
                  ...lesson,
                  moduleID: courseModule.id,
                  moduleOrder: courseModule.order,
                  lessonOrder: moduleLesson.order,
                  overallIndex: lessonIndex++, // Add overall position in journey
                  moduleTitle: courseModule.title, // Add module title for reference
                  faqItems: await fetchFAQItems(moduleLesson.lessonID),
                };
              })
            );

            allLessons = [...allLessons, ...moduleLessons.filter(lesson => lesson !== null)];
          }

          // After setting flattened lessons, force a progress refresh
          await fetchProgress();
        }
      } catch (error) {
        console.error('Error initializing journey:', error);
      } finally {
        setIsLocalLoading(false);
      }
    };

    initializeJourney();
  }, [isOpen, courseData, videoURLsData]);

  // Return null if still loading
  if (isLoading || currentLessonIndex === null || !currentLesson) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        className="journey-modal"
        overlayClassName="journey-modal-overlay"
      >
        <div className="loading-container">
          <div>Loading journey...</div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="journey-modal"
      overlayClassName="journey-modal-overlay"
      style={{
        overlay: {
          zIndex: 1100,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
        },
      }}
    >
      {!isDataReady ? (
        <div className="loading-container">
          <div>Loading journey data...</div>
        </div>
      ) : (
        <div className="journey-modal-content">
          <div className="modal-scroll-content">
            <div className="journey-progress-bar">
              <span className="total-days">
                  {isJourneyComplete() 
                      ? '' 
                      : `${totalDays} ${courseConfig?.totalLabel || 'Days'}`}
              </span>

              <div className="progress-track">
                <div
                  className="progress-fill"
                  style={{
                    width: isJourneyComplete() 
                        ? '100%' 
                        : `${(currentDay / totalDays) * 100}%`,
                  }}
                />
                <div
                  className="day-indicator current"
                  style={{
                    '--progress-position': isJourneyComplete()
                        ? '100%'
                        : `${(currentDay / totalDays) * 100}%`,
                  }}
                >
                  {isJourneyComplete() 
                      ? (courseConfig?.completionMessage || 'Journey Complete!') 
                      : `${courseConfig?.dayLabel || 'Day'} ${currentDay}`}
                </div>
              </div>
            </div>

            <div className="video-section">
              <div className="video-metadata">
                <div className="video-title-instructor">
                  <h1 className="video-title">{currentLesson?.video?.title || currentLesson?.title}</h1>
                  <p className="video-instructor">
                    Instructor:{' '}
                    {instructors
                      ? instructors.find(
                          (instructor) => instructor.id === currentLesson?.video?.teacherVideosId
                        )?.name
                      : 'Unknown Instructor'}
                  </p>
                </div>
                <p className="video-description">{currentLesson?.video?.description}</p>
                <div className="video-actions">
                  <button
                    className="action-button favorite-button"
                    onClick={() => onFavoriteToggle(currentLesson?.video?.id)}
                  >
                    <FontAwesomeIcon
                      icon={faHeart}
                      className={favorites?.includes(currentLesson?.video?.id) ? 'favorite' : ''}
                    />
                    {favorites?.includes(currentLesson?.video?.id) ? ' Remove Favorite' : ' Add Favorite'}
                  </button>
                  <button className="action-button share-button" onClick={handleShareClick}>
                    <FontAwesomeIcon icon={faLink} className="share-icon" />
                    &nbsp;Copy Link
                  </button>
                </div>
              </div>

              {isLoadingVideo ? (
                <div>Loading video...</div>
              ) : selectedVideoUrl ? (
                <div className="video-wrapper" key={`wrapper-${currentLesson?.id}`}>
                  <VideoPlayer
                    key={`video-${currentLesson?.id}`}
                    videoUrl={selectedVideoUrl}
                    poster={posterUrls[currentLesson.video.id]}
                    captions={selectedCaptions}
                    onComplete={() => handleProgressUpdate(currentLesson.id, true)}
                    progress={progress}
                  />
                </div>
              ) : (
                <div>No video selected or video not available.</div>
              )}
            </div>

            <div className="journey-controls">
              <button
                className={`progress-button ${progress?.completed ? 'completed' : ''}`}
                onClick={() => handleProgressUpdate(currentLesson.id, !progress?.completed)}
              >
                <FontAwesomeIcon icon={progress?.completed ? faCheck : faCircle} className="progress-icon" />
                {progress?.completed ? 'Completed' : 'Mark Complete'}
              </button>

              <div className="view-toggles">
                <button
                  className="toggle-button"
                  onClick={() => {
                    setIsSliderVisible(!isSliderVisible);
                    if (isFAQVisible) setIsFAQVisible(false);
                    if (isToolsVisible) setIsToolsVisible(false);
                  }}
                >
                  {isSliderVisible ? 'Hide Journey' : 'Show Journey'}
                </button>

                {currentLesson?.faqItems?.length > 0 && (
                  <button
                    className="toggle-button"
                    onClick={() => {
                      setIsFAQVisible(!isFAQVisible);
                      if (isSliderVisible) setIsSliderVisible(false);
                      if (isToolsVisible) setIsToolsVisible(false);
                    }}
                  >
                    {isFAQVisible ? 'Hide FAQ' : 'Show FAQ'}
                  </button>
                )}

                <button
                  className="toggle-button"
                  onClick={() => {
                    setIsToolsVisible(!isToolsVisible);
                    if (isSliderVisible) setIsSliderVisible(false);
                    if (isFAQVisible) setIsFAQVisible(false);
                  }}
                >
                  {isToolsVisible ? 'Hide Tools' : 'Show Tools'}
                </button>
              </div>
            </div>

            {isSliderVisible && (
              <>
                <div className={`journey-slider ${isAtEnd ? 'at-end' : ''} ${currentLessonIndex >= flattenedLessons.length - 2 ? 'near-end' : ''}`}>
                  <Slider ref={sliderRef} {...sliderSettings}>
                    {flattenedLessons.map((lesson, index) => (
                      <div key={lesson.id} className="journey-slide">
                        <div className="day-indicator">
                          {courseConfig?.dayLabel || 'Day'} {getDayNumber(lesson, index)}
                          <FontAwesomeIcon
                            icon={faCheck}
                            className={`completion-check ${lessonProgress[lesson.id]?.completed ? 'completed' : ''}`}
                          />
                        </div>

                        <Thumbnail
                          video={lesson.video}
                          title={lesson.video?.title || lesson.title}
                          category={lesson.video?.category?.name}
                          date={lesson.video?.date}
                          duration={lesson.video?.duration}
                          image={posterUrls[lesson.video?.id] || lesson.video?.poster}
                          instructorImage={instructorImages[lesson.video?.teacherVideosId]}
                          instructor={
                            instructors.find((i) => i.id === lesson.video?.teacherVideosId)?.name || ''
                          }
                          favorites={favorites}
                          isFavorite={favorites.includes(lesson.video?.id)}
                          onFavoriteToggle={onFavoriteToggle}
                          onClick={() => handleLessonChange(index)}
                          isCompleted={lessonProgress[lesson.id]?.completed}
                          isLocked={!lessonProgress[lesson.id]?.completed && getDayNumber(lesson, index) > currentDay}
                          inSlider={true}
                        />
                      </div>
                    ))}
                    {/* Add invisible placeholder slides for proper final slide positioning */}
                    {[1, 2].map((i) => (
                      <div key={`placeholder-${i}`} className="journey-slide placeholder-slide">
                        <div style={{ width: '100%', height: '200px', opacity: 0 }}></div>
                      </div>
                    ))}
                  </Slider>
                </div>
                <div className="journey-navigation">
                  <button 
                      className="journey-nav-button"
                      onMouseDown={() => startScrolling('prev')}
                      onMouseUp={stopScrolling}
                      onMouseLeave={stopScrolling}
                      onTouchStart={() => startScrolling('prev')}
                      onTouchEnd={stopScrolling}
                  >
                      <FontAwesomeIcon icon={faChevronLeft} /> Previous
                  </button>

                  <button 
                      className="journey-nav-button current" 
                      onClick={handleCurrentLessonClick}
                  >
                      <FontAwesomeIcon icon={faHome} /> Current Lesson
                  </button>

                  <button 
                      className="journey-nav-button"
                      onMouseDown={() => startScrolling('next')}
                      onMouseUp={stopScrolling}
                      onMouseLeave={stopScrolling}
                      onTouchStart={() => startScrolling('next')}
                      onTouchEnd={stopScrolling}
                  >
                      Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </>
            )}

            {isFAQVisible && currentLesson?.faqItems?.length > 0 && (
              <div className="faq-section">
                {currentLesson.faqItems.map((faq) => (
                  <div key={faq.id} className="faq-item">
                    <h3 className="faq-question">{faq.question}</h3>
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                ))}
              </div>
            )}

            {isToolsVisible && (
              <div className="video-tools-section">
                <div className="tools-content">
                  <Tools />
                </div>
              </div>
            )}
          </div>
          <button className="close-button" onClick={onRequestClose}>
            ×
          </button>
        </div>
      )}
    </Modal>
  );
};

export default JourneyModal;